import { useEffect, useState } from 'react';
import {
  Alert,
  Anchor,
  Badge,
  Box,
  Button,
  Divider,
  Group,
  Loader,
  NumberInput,
  Paper,
  PasswordInput,
  Stack,
  Switch,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import {
  IconDeviceFloppy,
  IconInfoCircle,
  IconPlug,
  IconWand,
} from '@tabler/icons-react';
import type { IntegrationSettingsDto } from '@expeditor/shared';
import { integrationsApi } from '../../shared/api/integrations';

type FormState = {
  dadataEnabled: boolean;
  dadataApiKey: string;
  edoEnabled: boolean;
  edoToken: string;
  edoApiUrl: string;
  edoWebUrl: string;
  edoTestMode: boolean;
  edoTestApiUrl: string;
  edoTestWebUrl: string;
  edoTestToken: string;
  edoPollIntervalSec: number | '';
  pdfVizUrl: string;
};

const emptyForm: FormState = {
  dadataEnabled: false,
  dadataApiKey: '',
  edoEnabled: false,
  edoToken: '',
  edoApiUrl: '',
  edoWebUrl: '',
  edoTestMode: false,
  edoTestApiUrl: '',
  edoTestWebUrl: '',
  edoTestToken: '',
  edoPollIntervalSec: 60,
  pdfVizUrl: '',
};

const dtoToForm = (d: IntegrationSettingsDto): FormState => ({
  dadataEnabled: d.dadataEnabled,
  dadataApiKey: '',
  edoEnabled: d.edoEnabled,
  edoToken: '',
  edoApiUrl: d.edoApiUrl ?? '',
  edoWebUrl: d.edoWebUrl ?? '',
  edoTestMode: d.edoTestMode,
  edoTestApiUrl: d.edoTestApiUrl ?? '',
  edoTestWebUrl: d.edoTestWebUrl ?? '',
  edoTestToken: '',
  edoPollIntervalSec: d.edoPollIntervalSec,
  pdfVizUrl: d.pdfVizUrl ?? '',
});

export function IntegrationsPage() {
  const [dto, setDto] = useState<IntegrationSettingsDto | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; error: string | null } | null>(null);
  const [edoTesting, setEdoTesting] = useState(false);
  const [edoTestResult, setEdoTestResult] = useState<{ ok: boolean; error: string | null } | null>(
    null,
  );
  // Проверка тестовой среды ЭДО — отдельная кнопка/результат.
  const [edoTestEnvTesting, setEdoTestEnvTesting] = useState(false);
  const [edoTestEnvResult, setEdoTestEnvResult] = useState<{
    ok: boolean;
    error: string | null;
  } | null>(null);

  useEffect(() => {
    let cancelled = false;
    integrationsApi
      .getSettings()
      .then((d) => {
        if (cancelled) return;
        setDto(d);
        setForm(dtoToForm(d));
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Не удалось загрузить'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSavedAt(null);
    setSaving(true);
    try {
      const updated = await integrationsApi.updateSettings({
        dadataEnabled: form.dadataEnabled,
        // '' — не меняем (если оставлен пустым)
        dadataApiKey: form.dadataApiKey === '' ? undefined : form.dadataApiKey,
        edoEnabled: form.edoEnabled,
        edoToken: form.edoToken === '' ? undefined : form.edoToken,
        edoApiUrl: form.edoApiUrl,
        edoWebUrl: form.edoWebUrl,
        edoTestMode: form.edoTestMode,
        edoTestApiUrl: form.edoTestApiUrl,
        edoTestWebUrl: form.edoTestWebUrl,
        edoTestToken: form.edoTestToken === '' ? undefined : form.edoTestToken,
        edoPollIntervalSec: form.edoPollIntervalSec === '' ? undefined : form.edoPollIntervalSec,
        pdfVizUrl: form.pdfVizUrl,
      });
      setDto(updated);
      setForm(dtoToForm(updated));
      setSavedAt(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось сохранить');
    } finally {
      setSaving(false);
    }
  };

  const runTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await integrationsApi.testDadata();
      setTestResult(r);
      const fresh = await integrationsApi.getSettings();
      setDto(fresh);
    } catch (err) {
      setTestResult({ ok: false, error: err instanceof Error ? err.message : 'Ошибка' });
    } finally {
      setTesting(false);
    }
  };

  const runEdoTest = async () => {
    setEdoTesting(true);
    setEdoTestResult(null);
    try {
      setEdoTestResult(await integrationsApi.testEdo('prod'));
    } catch (err) {
      setEdoTestResult({ ok: false, error: err instanceof Error ? err.message : 'Ошибка' });
    } finally {
      setEdoTesting(false);
    }
  };

  const runEdoTestEnvTest = async () => {
    setEdoTestEnvTesting(true);
    setEdoTestEnvResult(null);
    try {
      setEdoTestEnvResult(await integrationsApi.testEdo('test'));
    } catch (err) {
      setEdoTestEnvResult({ ok: false, error: err instanceof Error ? err.message : 'Ошибка' });
    } finally {
      setEdoTestEnvTesting(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <Box>
      <Title order={3} mb="md">
        Интеграции
      </Title>

      <Paper withBorder p="lg" radius="md" mb="md">
        <Stack gap="md">
          <Group justify="space-between">
            <div>
              <Title order={4}>
                <IconPlug size={18} style={{ verticalAlign: 'middle' }} /> DaData
              </Title>
              <Text size="sm" c="dimmed">
                Подсказки реквизитов по ИНН / ОГРН / названию на форме регистрации
                клиентов. Бесплатный тариф — до 10 000 запросов в день.
              </Text>
            </div>
            {dto?.dadataApiKeySet ? (
              <Badge color="green" variant="light">
                ключ сохранён
              </Badge>
            ) : (
              <Badge color="gray" variant="light">
                ключ не задан
              </Badge>
            )}
          </Group>

          <form onSubmit={onSubmit}>
            <Stack>
              <Switch
                label="Включить подсказки DaData на форме регистрации"
                checked={form.dadataEnabled}
                onChange={(e) => setForm({ ...form, dadataEnabled: e.currentTarget.checked })}
              />

              <Group align="flex-end" gap="sm">
                <div style={{ flex: 1 }}>
                  <PasswordInput
                    label="API-ключ DaData"
                    description={
                      <>
                        Получить можно бесплатно в личном кабинете на{' '}
                        <Anchor href="https://dadata.ru/api/" target="_blank">
                          dadata.ru/api/
                        </Anchor>{' '}
                        (нужен только API Key, Secret Key не используется).
                        {dto?.dadataApiKeySet && ' Оставьте поле пустым, чтобы не менять текущий.'}
                      </>
                    }
                    value={form.dadataApiKey}
                    onChange={(e) => setForm({ ...form, dadataApiKey: e.currentTarget.value })}
                    autoComplete="off"
                  />
                </div>
                <Button
                  variant="default"
                  onClick={runTest}
                  loading={testing}
                  leftSection={<IconWand size={16} />}
                >
                  Проверить
                </Button>
              </Group>
              {testResult && (
                <Text size="sm" c={testResult.ok ? 'green' : 'red'}>
                  {testResult.ok ? 'DaData отвечает корректно.' : `Ошибка: ${testResult.error}`}
                </Text>
              )}
              <Text size="xs" c="dimmed">
                «Проверить» отправит на DaData тестовый запрос сохранённым ключом (1 лимит из
                суточной квоты).
                {dto?.dadataLastTestedAt && (
                  <>
                    {' '}Последний тест:{' '}
                    {new Date(dto.dadataLastTestedAt).toLocaleString('ru-RU')} —{' '}
                    <strong>{dto.dadataLastTestStatus === 'ok' ? 'успех' : 'ошибка'}</strong>
                    {dto.dadataLastTestError ? ` (${dto.dadataLastTestError})` : ''}
                  </>
                )}
              </Text>

              <Divider label="Интеграция с ЭДО.МИГ24" labelPosition="left" mt="sm" />
              <Group justify="space-between">
                <Text size="sm" c="dimmed">
                  Обмен электронными документами с привлекаемыми исполнителями. Токен авторизации
                  (GUID) выдаётся в личном кабинете ЭДО.МИГ24; по нему система сможет искать
                  абонентов по ИНН и отправлять документы.
                </Text>
                {dto?.edoTokenSet ? (
                  <Badge color="green" variant="light">
                    токен сохранён
                  </Badge>
                ) : (
                  <Badge color="gray" variant="light">
                    токен не задан
                  </Badge>
                )}
              </Group>
              <Switch
                label="Включить интеграцию с ЭДО.МИГ24"
                checked={form.edoEnabled}
                onChange={(e) => setForm({ ...form, edoEnabled: e.currentTarget.checked })}
              />
              <TextInput
                label="Адрес API ЭДО.МИГ24"
                description="Базовый URL сервиса, например https://api.mig24.online"
                placeholder="https://api.mig24.online"
                value={form.edoApiUrl}
                onChange={(e) => setForm({ ...form, edoApiUrl: e.currentTarget.value })}
              />
              <TextInput
                label="Адрес веб-кабинета МИГ24 (страница подписания)"
                description="Из него ссылка на подписание: {адрес}/transportations/{id}. По умолчанию https://epd.mig24.online"
                placeholder="https://epd.mig24.online"
                value={form.edoWebUrl}
                onChange={(e) => setForm({ ...form, edoWebUrl: e.currentTarget.value })}
              />
              <Group align="flex-end" gap="sm">
                <div style={{ flex: 1 }}>
                  <PasswordInput
                    label="Токен авторизации ЭДО (GUID)"
                description={
                  dto?.edoTokenSet
                    ? 'Оставьте поле пустым, чтобы не менять текущий токен.'
                    : 'Формат GUID, например 3F2504E0-4F89-11D3-9A0C-0305E82C3301.'
                }
                placeholder="00000000-0000-0000-0000-000000000000"
                    value={form.edoToken}
                    onChange={(e) => setForm({ ...form, edoToken: e.currentTarget.value })}
                    autoComplete="off"
                  />
                </div>
                <Button
                  variant="default"
                  onClick={runEdoTest}
                  loading={edoTesting}
                  leftSection={<IconWand size={16} />}
                >
                  Проверить
                </Button>
              </Group>
              {dto?.edoTokenSet && (
                <Text size="xs" c="dimmed">
                  Задан: {dto.edoTokenHint ?? '••••'}
                  {dto.edoTokenSetAt &&
                    ` · сохранён ${new Date(dto.edoTokenSetAt).toLocaleString('ru-RU')}`}
                </Text>
              )}
              {edoTestResult && (
                <Text size="sm" c={edoTestResult.ok ? 'green' : 'red'}>
                  {edoTestResult.ok
                    ? 'API ЭДО доступен, токен принят.'
                    : `Ошибка: ${edoTestResult.error}`}
                </Text>
              )}

              <Divider label="Тестовая среда ЭДО и опрос статусов" labelPosition="left" mt="sm" />
              <Switch
                label="Тестовый режим"
                description="Когда включён — все обращения к ЭДО идут в тестовую среду (адрес и токен ниже)."
                checked={form.edoTestMode}
                onChange={(e) => setForm({ ...form, edoTestMode: e.currentTarget.checked })}
              />
              <TextInput
                label="Адрес API тестовой среды"
                placeholder="https://test-api.ntssoft.ru"
                value={form.edoTestApiUrl}
                onChange={(e) => setForm({ ...form, edoTestApiUrl: e.currentTarget.value })}
              />
              <TextInput
                label="Адрес веб-кабинета МИГ24 (тестовый)"
                description="Ссылка на подписание в тест-режиме. По умолчанию https://epd.mig24.online"
                placeholder="https://epd.mig24.online"
                value={form.edoTestWebUrl}
                onChange={(e) => setForm({ ...form, edoTestWebUrl: e.currentTarget.value })}
              />
              <Group align="flex-end" gap="sm">
                <div style={{ flex: 1 }}>
                  <PasswordInput
                    label="Токен тестовой среды (GUID)"
                    description={
                      dto?.edoTestTokenSet
                        ? 'Оставьте поле пустым, чтобы не менять текущий тестовый токен.'
                        : 'Токен API тестового контура ЭДО.'
                    }
                    placeholder="00000000-0000-0000-0000-000000000000"
                    value={form.edoTestToken}
                    onChange={(e) => setForm({ ...form, edoTestToken: e.currentTarget.value })}
                    autoComplete="off"
                  />
                </div>
                <Button
                  variant="default"
                  onClick={runEdoTestEnvTest}
                  loading={edoTestEnvTesting}
                  leftSection={<IconWand size={16} />}
                >
                  Проверить
                </Button>
              </Group>
              {dto?.edoTestTokenSet && (
                <Text size="xs" c="dimmed">
                  Задан: {dto.edoTestTokenHint ?? '••••'}
                  {dto.edoTestTokenSetAt &&
                    ` · сохранён ${new Date(dto.edoTestTokenSetAt).toLocaleString('ru-RU')}`}
                </Text>
              )}
              {edoTestEnvResult && (
                <Text size="sm" c={edoTestEnvResult.ok ? 'green' : 'red'}>
                  {edoTestEnvResult.ok
                    ? 'Тестовая среда ЭДО доступна, токен принят.'
                    : `Ошибка: ${edoTestEnvResult.error}`}
                </Text>
              )}
              <NumberInput
                label="Интервал опроса статусов, сек"
                description="Как часто забирать события статусов документов из ЭДО (мин. 10)."
                min={10}
                max={86400}
                value={form.edoPollIntervalSec}
                onChange={(v) =>
                  setForm({ ...form, edoPollIntervalSec: v === '' ? '' : Number(v) })
                }
              />

              <TextInput
                label="Адрес сервиса визуализации XML→PDF"
                description="Куда слать XML для рендера PDF (напр. https://xml2pdf.mig24.online). Пусто — по умолчанию."
                placeholder="https://xml2pdf.mig24.online"
                value={form.pdfVizUrl}
                onChange={(e) => setForm({ ...form, pdfVizUrl: e.currentTarget.value })}
              />

              {error && (
                <Alert color="red" icon={<IconInfoCircle size={16} />}>
                  {error}
                </Alert>
              )}
              {savedAt && (
                <Alert color="green" icon={<IconDeviceFloppy size={16} />}>
                  Сохранено в {savedAt.toLocaleTimeString('ru-RU')}.
                </Alert>
              )}

              <Group justify="flex-end">
                <Button type="submit" loading={saving} leftSection={<IconDeviceFloppy size={16} />}>
                  Сохранить
                </Button>
              </Group>
            </Stack>
          </form>
        </Stack>
      </Paper>
    </Box>
  );
}
